import React, { useState, useEffect } from 'react';
import { VStack, Stack, Box, Container, useToast } from '@chakra-ui/react';
import {
  TextInput,
  NumberInput,
  ComboBox,
  Button,
  PasswordInput,
  BodyText,
} from '@spotoninc/nexus-react';
import { LockIcon } from '@spotoninc/nexus-web-assets';
import { RegistrationHeader } from './RegistrationHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiEndpoint from '../config/data';
export const ContactDetails = ({
  companyData,
  setStep,
  setAccountingSystem,
}) => {
  const [searchParams] = useSearchParams();

  // Initialize form state with safe defaults
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: companyData?.companyName || '',
    phoneNumber: '',
    zipCode: '',
    accountingFormat: 'monthly',
    periodSetting: '',
    password: '',
    rePassword: '',
  });

  // Initialize form data when component mounts
  useEffect(() => {
    // Initialize form data with values from companyData if available
    if (companyData) {
      // Create a new form data object with values from companyData
      const newFormData = {
        firstName: companyData.firstName || '',
        lastName: companyData.lastName || '',
        companyName: companyData.companyName || '',
        phoneNumber: companyData.phone || '',
        zipCode: companyData.zipCode ? String(companyData.zipCode) : '',
        accountingFormat: 'monthly',
        periodSetting: '',
        password: '',
        rePassword: '',
      };

      // Set the form data directly instead of using a callback
      setFormData(newFormData);

      // If accounting_software is provided and setAccountingSystem function is provided
      if (companyData.accounting_software && setAccountingSystem) {
        // Map accounting_software string to the corresponding value
        if (companyData.accounting_software === 'quickbooks-online') {
          setAccountingSystem(1);
        } else if (companyData.accounting_software === 'xero') {
          setAccountingSystem(2);
        } else if (companyData.accounting_software === 'restaurant365') {
          setAccountingSystem(3);
        }
      }
    }
  }, [searchParams, companyData, setAccountingSystem]);
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const [showPeriodSetting, setShowPeriodSetting] = useState(false);
  const periodCalendarOptions = [
    { label: '13 x 4', value: '13x4' },
    { label: '4-4-5', value: '4-4-5' },
    { label: '4-5-4', value: '4-5-4' },
    { label: '5-4-4', value: '5-4-4' },
  ];
  const accountingFormatOptions = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Periods', value: 'periods' },
  ];

  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Company Name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    // Phone Number validation
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    // Zip Code validation
    if (!formData.zipCode) {
      newErrors.zipCode = 'Zip code is required';
    } else if (!/^\d{5}$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid 5-digit zip code';
    }

    // Period Setting validation (only if accounting format is periods)
    if (formData.accountingFormat === 'periods' && !formData.periodSetting) {
      newErrors.periodSetting = 'Period calendar type is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    if (!formData.rePassword) {
      newErrors.rePassword = 'Please re-enter your password';
    }
    if (
      formData.password &&
      formData.rePassword &&
      formData.password !== formData.rePassword
    ) {
      newErrors.rePassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = field => event => {
    // Check if event and event.target exist before accessing value
    if (event && event.target) {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }
      // Clear both password errors when either password is changed
      if (field === 'password' || field === 'rePassword') {
        setErrors(prev => ({
          ...prev,
          password: undefined,
          rePassword: undefined,
        }));
      }
    } else {
      // Invalid event object in handleInputChange
    }
  };

  const handleComboBoxChange = field => (_, value) => {
    // Ensure value is defined before using it
    if (value !== undefined) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
      if (field === 'accountingFormat') {
        setShowPeriodSetting(value === 'periods');
        if (value !== 'periods') {
          setErrors(prev => ({
            ...prev,
            periodSetting: undefined,
          }));
        }
      }
      // Clear error when user makes a selection
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }
    } else {
      // Invalid value in handleComboBoxChange
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return; // Don't proceed if validation fails
    }

    setIsLoading(true);

    try {
      // Create FormData object for API submission
      const apiFormData = new FormData();

      // Map our form fields to the expected API fields
      apiFormData.append('firstName', formData.firstName);
      apiFormData.append('lastName', formData.lastName);
      apiFormData.append('companyName', formData.companyName);
      apiFormData.append('companyPhone', formData.phoneNumber);
      apiFormData.append('companyEmail', companyData?.companyEmail || '');
      apiFormData.append('companyZipCode', formData.zipCode);

      // Add required fields with default values
      apiFormData.append('companyAddr1', '');
      apiFormData.append('companyAddr2', '');
      apiFormData.append('companyCountry', 'United States');
      apiFormData.append('companyCity', '');
      apiFormData.append('companyState', '');

      // User fields (duplicating company info as per reference code)
      apiFormData.append('userPhone', formData.phoneNumber);
      apiFormData.append('userAddr1', '');
      apiFormData.append('userAddr2', '');
      apiFormData.append('userCountry', 'United States');
      apiFormData.append('userEmail', companyData?.companyEmail || '');
      apiFormData.append('userCity', '');
      apiFormData.append('userZipCode', formData.zipCode);
      apiFormData.append('userState', '');

      // Password
      apiFormData.append('pass', formData.password);

      // Add code from companyData if available, or from URL if not
      const codeFromUrl = searchParams.get('code');

      if (companyData && companyData.code) {
        apiFormData.append('code', companyData.code);
      } else if (codeFromUrl) {
        apiFormData.append('code', codeFromUrl);
      }

      // Add blid from companyData if available, or from URL if not
      const blidFromUrl = searchParams.get('blid');

      if (companyData && companyData.blid && companyData.blid.trim() !== '') {
        apiFormData.append('blid', companyData.blid.trim());
      } else if (blidFromUrl && blidFromUrl.trim() !== '') {
        apiFormData.append('blid', blidFromUrl.trim());
      }

      // Add period option if selected
      if (formData.accountingFormat === 'periods' && formData.periodSetting) {
        apiFormData.append('periodOption', formData.periodSetting);
        // Also update the accounting system in parent component
        if (setAccountingSystem) {
          setAccountingSystem(formData.periodSetting);
        }
      }

      // Prepare form data for API submission

      // Create an AbortController to handle timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      try {
        // Make API call with timeout
        const response = await fetch(apiEndpoint + '/api/regData/', {
          method: 'POST',
          body: apiFormData,
          signal: controller.signal,
        });

        // Clear the timeout since the request completed
        clearTimeout(timeoutId);

        // Check if the response is ok
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // Process the response data
        if (data.registration_status === 'passed') {
          toast({
            title: 'Registration successful!',
            description: 'You can now log in with your credentials.',
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
          navigate('/login');
        } else {
          toast({
            title: 'Registration failed',
            description: data.registration_status || 'Please try again later.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        }
      } catch (fetchError) {
        // Handle AbortController timeout
        if (fetchError.name === 'AbortError') {
          toast({
            title: 'Registration failed',
            description:
              'The request timed out. Please check your internet connection and try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        } else {
          toast({
            title: 'Registration failed',
            description:
              fetchError.message ||
              'An unexpected error occurred during the request.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: 'An unexpected error occurred. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
      <VStack spacing={6} align="stretch" width="100%">
        <RegistrationHeader
          title="Contact Details"
          description="Please enter your contact details."
        />
        <VStack spacing={6} align="stretch" width="100%">
          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={{ base: 4, md: 6 }}
            width="100%"
          >
            <TextInput
              label="First Name"
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              errorMessage={errors.firstName}
            />
            <TextInput
              label="Last Name"
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              errorMessage={errors.lastName}
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={{ base: 4, md: 6 }}
            width="100%"
          >
            <TextInput
              label="Company Name"
              value={formData.companyName}
              onChange={handleInputChange('companyName')}
              errorMessage={errors.companyName}
            />
            <NumberInput
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={handleInputChange('phoneNumber')}
              errorMessage={errors.phoneNumber}
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={{ base: 4, md: 6 }}
            width="100%"
          >
            <NumberInput
              label="Zip Code"
              value={formData.zipCode}
              onChange={handleInputChange('zipCode')}
              maxLength={5}
              errorMessage={errors.zipCode}
            />
            <ComboBox
              label="Accounting Format"
              options={accountingFormatOptions}
              value={formData.accountingFormat}
              onChange={handleComboBoxChange('accountingFormat')}
              errorMessage={errors.accountingFormat}
            />
          </Box>

          {showPeriodSetting && (
            <Box width={{ base: '100%', md: 'calc(50% - 12px)' }}>
              <ComboBox
                label="Period Calendar Type"
                options={periodCalendarOptions}
                value={formData.periodSetting}
                onChange={handleComboBoxChange('periodSetting')}
                errorMessage={errors.periodSetting}
              />
            </Box>
          )}

          <VStack spacing={4} align="stretch" width="100%" mt={4} minH="150px">
            <BodyText size="medium" style={{ marginBottom: '12px' }}>
              Create a secure password unique to you.
            </BodyText>
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={{ base: 4, md: 6 }}
              width="100%"
            >
              <PasswordInput
                label="Password"
                placeholder=""
                value={formData.password}
                onChange={handleInputChange('password')}
                leadingIcon={() => <LockIcon />}
                style={{
                  width: '100%',
                }}
                errorMessage={errors.password}
              />
              <PasswordInput
                label="Re-enter Password"
                placeholder=""
                value={formData.rePassword}
                onChange={handleInputChange('rePassword')}
                leadingIcon={() => <LockIcon />}
                style={{
                  width: '100%',
                }}
                errorMessage={errors.rePassword}
              />
            </Box>
          </VStack>

          <Stack direction={{ base: 'column', md: 'row' }} spacing={6}>
            <Button variant="tonal" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              style={{ paddingLeft: '50px', paddingRight: '50px' }}
              isLoading={isLoading}
              loadingText="Signing up..."
              className="centered-spinner-button"
            >
              Sign up
            </Button>
          </Stack>
        </VStack>
      </VStack>
    </Container>
  );
};
